import type {
    ApprovalCaseDetail,
    ApprovalDecision,
    ApprovalTask,
    BulkDecideResult,
    HttpPort,
    IApprovalService,
    RuleLintResult,
} from "../contracts";

// ApprovalService — wraps /api/approval/*. Mirrors the original frontend's
// thin client (services/ApprovalService.ts). The header bell + the approval
// inbox view both consume this.
export class ApprovalService implements IApprovalService {
    constructor(private readonly http: HttpPort) {}

    async getInbox(): Promise<ApprovalTask[]> {
        const res = await this.http.get<{ records?: ApprovalTask[] }>("/api/approval/inbox");
        return res.records ?? [];
    }

    async getCase(caseId: string): Promise<ApprovalCaseDetail> {
        return this.http.get<ApprovalCaseDetail>(`/api/approval/cases/${encodeURIComponent(caseId)}`);
    }

    async decide(caseId: string, decision: ApprovalDecision, comment = ""): Promise<void> {
        await this.http.post(`/api/approval/cases/${encodeURIComponent(caseId)}/decide`, {
            decision,
            comment,
        });
    }

    async bulkDecide(caseIds: string[], decision: ApprovalDecision, comment = ""): Promise<BulkDecideResult> {
        return this.http.post<BulkDecideResult>("/api/approval/cases/bulk-decide", {
            case_ids: caseIds,
            decision,
            comment,
        });
    }

    async lintRule(expr: string, domain = "", subjectModel = ""): Promise<RuleLintResult> {
        return this.http.post<RuleLintResult>("/api/approval/rules/lint", {
            expr,
            domain,
            subject_model: subjectModel,
        });
    }

    async delegate(caseId: string, delegateUserId: string, comment = ""): Promise<void> {
        await this.http.post(`/api/approval/cases/${encodeURIComponent(caseId)}/delegate`, {
            delegate_to_user_id: delegateUserId,
            comment,
        });
    }
}

// ── UI helpers ──────────────────────────────────────────────────────────────

export function slaBucket(dueAt: string | undefined): "ok" | "warn" | "overdue" {
    if (!dueAt) return "ok";
    const due = new Date(dueAt).getTime();
    const now = Date.now();
    const remaining = due - now;
    if (remaining <= 0) return "overdue";
    if (remaining <= 6 * 60 * 60 * 1000) return "warn";
    return "ok";
}

export function formatDueAt(dueAt: string | undefined): string {
    if (!dueAt) return "—";
    const due = new Date(dueAt);
    if (Number.isNaN(due.getTime())) return "—";
    const diffMs = due.getTime() - Date.now();
    const absHours = Math.abs(diffMs / 3_600_000);
    if (absHours < 1) {
        const minutes = Math.round(absHours * 60);
        return diffMs >= 0 ? `due in ${minutes}m` : `overdue ${minutes}m`;
    }
    if (absHours < 48) {
        return diffMs >= 0 ? `due in ${Math.round(absHours)}h` : `overdue ${Math.round(absHours)}h`;
    }
    return due.toLocaleDateString();
}
